import cx from 'classnames';
import Tooltip from 'rc-tooltip';
import _ from 'lodash';
import { cloneElement, createElement, useEffect, useState, MouseEvent } from 'react';
import { useIsTouchDevice } from '../grid/hooks';

export type TooltipPlacement =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'leftTop'
  | 'leftBottom'
  | 'rightTop'
  | 'rightBottom';

export type TSimpleTooltipParams = {
  id?: string;
  tooltip: any;
  placement?: TooltipPlacement;
  children?: any;
  tag?: string;
  onClick?: Function;
  className?: string;
  tooltipClassName?: string;
  hideTooltip?: boolean;
  /**
   * The hideTooltip prop prevents the remounting of child components; it keeps the tooltip in the DOM but makes it invisible.
   */
  notVisibleTooltip?: boolean;
  style?: any;
  overlayStyle?: any;
  mouseLeaveDelay?: number;
  opacity?: number;
  renderJustChildren?: boolean;
  overlayClassName?: string;
  getTooltipContainer?: () => HTMLElement;
  visible?: boolean;
  trigger?: 'click' | 'hover' | 'focus' | Array<'click' | 'hover' | 'focus'>;
  clickableTooltip?: boolean;
  showArrow?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  mouseEnterDelay?: number;
  destroyTooltipOnHide?:
    | boolean
    | {
        keepParent?: boolean;
      };
  noPaddingInner?: boolean;
  preventClickOnTouchIfNotOpen?: boolean;
};

export function SimpleTooltip(props: TSimpleTooltipParams) {
  const child = createElement(props.tag || 'div', { ..._.pick(props, 'className', 'onClick') }, props.children);

  if (props.hideTooltip) {
    // this prop lets us skip using the tooltip conditionally without reworking the react
    return child;
  }

  return (
    <div style={props.style} className={cx('rio-simple-tt', props.className, props.tooltipClassName)}>
      <div className="top">
        {props.tooltip}
        <i className="downarrow"></i>
      </div>
      {child}
    </div>
  );
}

// Just like SimpleTooltip but removes tooltipClassName and changes
// how things behave when tooltip is hidden. This should be what
// we move everything to - but creating as a separate class so we
// can test the changes without breaking things elsewhere.
export function SimpleTooltip_NEXT(props: TSimpleTooltipParams) {
  if (props.hideTooltip) {
    // this prop lets us skip using the tooltip conditionally without reworking the react
    return createElement(props.tag || 'div', { ..._.pick(props, 'className', 'onClick') }, props.children);
  }

  return (
    <div style={props.style} className={cx('rio-simple-tt', props.className)} onClick={props.onClick as any}>
      <div className="top">
        {props.tooltip}
        <i className="downarrow"></i>
      </div>
      {props.children}
    </div>
  );
}

export function ComplexTooltip(props: TSimpleTooltipParams) {
  const isTouchDevice = useIsTouchDevice();

  const child = props.renderJustChildren
    ? props.children
    : createElement(props.tag || 'div', { ..._.pick(props, 'className', 'onClick') }, props.children);

  // we need to wait for tooltip to align before setting opacity
  // or user will see the tooltip in the top left corner for a ms
  const [isAligned, setIsAligned] = useState(false);
  const [immediateOpen, setImmediateOpen] = useState(false);

  useEffect(() => {
    if (props.visible === false) {
      setIsAligned(false);
    }
  }, [props.visible]);

  if (props.hideTooltip) {
    // this prop lets us skip using the tooltip conditionally without reworking the react
    return child;
  }

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (isTouchDevice && props.preventClickOnTouchIfNotOpen && !immediateOpen) {
      e.preventDefault();
      e.stopPropagation();
    }

    props.onClick?.(e);
  };

  const elem = props.renderJustChildren
    ? cloneElement(child, {
        onClick: (e: MouseEvent<HTMLDivElement>) => {
          child.props.onClick?.(e);
          handleClick(e);
        }
      })
    : createElement(
        props.tag || 'div',
        { style: props.style, className: props.className, onClick: handleClick },
        child
      );

  const overlayStyle = {
    ...props.overlayStyle,
    opacity: isAligned ? props.overlayStyle?.opacity || props.opacity : 0,
    zIndex: 100001
  };

  const getTooltipContainer = () => {
    return document.fullscreenElement || document.body || props.getTooltipContainer?.() || document.body;
  };

  return (
    <Tooltip
      id={props.id}
      placement={props.placement}
      overlayClassName={cx(props.overlayClassName, {
        'rio-tooltip-clickable': props.clickableTooltip,
        'rio-tooltip-no-arrow': props.showArrow === false,
        'rio-tooltip-no-padding-inner': props.noPaddingInner,
        'rio-tooltip-not-visible': props.notVisibleTooltip
      })}
      destroyTooltipOnHide={props.destroyTooltipOnHide || { keepParent: false }}
      transitionName={null}
      mouseLeaveDelay={props.mouseLeaveDelay || 0}
      mouseEnterDelay={props.mouseEnterDelay || 0}
      overlay={<div>{props.tooltip}</div>}
      overlayStyle={overlayStyle}
      getTooltipContainer={getTooltipContainer}
      onPopupAlign={() => setIsAligned(true)}
      onVisibleChange={(isVisible: boolean) => {
        if (!isVisible) {
          setIsAligned(false);
        }

        setTimeout(() => {
          setImmediateOpen(isVisible);
        }, 0);

        props.onVisibleChange?.(isVisible);
      }}
      {...('visible' in props ? { visible: props.visible } : {})}
      {...(props.trigger ? { trigger: props.trigger } : {})}
    >
      {elem}
    </Tooltip>
  );
}
